/**
 * ADM-SETTINGS-REPARTITION-SCALE.WIDGET
 * Edit one scale of the repartition widget : number of groups, ceils and labels
 * A label left empty keeps the translated default label
 */

import { withTranslation, WithTranslation } from "react-i18next"
import MultiRangeSlider from "multi-range-slider-react"
import Dropdown from "@/components/dropdown"
import TextInput from "@/components/text-input"
import {
  CEILS_REPARTITION_DEFAULT,
  CEILS_REPARTITION_MAX,
  CEILS_REPARTITION_MIN,
  CEILS_REPARTITION_SCALE_MAX,
  getRepartitionDefaultLabel
} from "@/utils/get-repartition-ceils.utils"

interface OwnProps {
  ceils: number[]
  labels: string[]
  isGollac: boolean
  title: string
  onChange: (ceils: number[], labels: string[]) => void
}

type Props = OwnProps & WithTranslation

function AdmSettingsRepartitionScaleWidget(props: Props) {
  const { t } = props

  const count: number = props.ceils.length + 1

  //Add or remove the middle group
  //Labels of the first and the last group are kept
  function editCount(nextCount: number) {
    if (nextCount === count) return

    if (nextCount === 3) {
      const ceilMax: number = Math.max(props.ceils[0] + 1, CEILS_REPARTITION_DEFAULT[1])
      props.onChange([props.ceils[0], Math.min(ceilMax, CEILS_REPARTITION_MAX)], [getLabel(0), "", getLabel(1)])
    } else {
      props.onChange([props.ceils[0]], [getLabel(0), getLabel(count - 1)])
    }
  }

  function editLabel(index: number, value: string) {
    const labels: string[] = Array.from({ length: count }, (_, i: number) => getLabel(i))
    labels[index] = value
    props.onChange(props.ceils, labels)
  }

  function getLabel(index: number): string {
    return props.labels[index] ?? ""
  }

  return (
    <div className="flex1 min-w-0">
      <div className="flex items-center">
        <b>{props.title}</b>
        <div className="width-20" />
        <Dropdown
          displayField="name"
          list={[
            { id: 2, name: t("account_ceil_repartition_groups_count", { value: 2 }) },
            { id: 3, name: t("account_ceil_repartition_groups_count", { value: 3 }) }
          ]}
          value={t("account_ceil_repartition_groups_count", { value: count })}
          active={count}
          onSelect={(e: any) => editCount(e.id)}
        />
      </div>

      <div className="height-10" />

      {count === 3 ? (
        <MultiRangeSlider
          min={CEILS_REPARTITION_MIN}
          max={CEILS_REPARTITION_MAX}
          step={1}
          ruler={false}
          minValue={props.ceils[0]}
          maxValue={props.ceils[1]}
          onInput={(e: any) => props.onChange([e.minValue, e.maxValue], props.labels)}
        />
      ) : (
        <div className="repartition-slider-single">
          <MultiRangeSlider
            min={CEILS_REPARTITION_MIN}
            max={CEILS_REPARTITION_SCALE_MAX}
            step={1}
            ruler={false}
            minValue={props.ceils[0]}
            maxValue={CEILS_REPARTITION_SCALE_MAX}
            onInput={(e: any) => props.onChange([e.minValue], props.labels)}
          />
        </div>
      )}

      <div className="height-10" />

      <div className="flex" style={{ gap: 10 }}>
        {Array.from({ length: count }, (_, i: number) => (
          <TextInput
            key={i}
            className="min-w-0 flex-1"
            isFullWidth
            value={getLabel(i)}
            placeholder={getRepartitionDefaultLabel(i, count, props.isGollac, false)}
            title={t("account_ceil_repartition_group_label", { value: i + 1 })}
            onChange={(e: any) => editLabel(i, e.value)}
          />
        ))}
      </div>
    </div>
  )
}

export default withTranslation()(AdmSettingsRepartitionScaleWidget)
